import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Phone } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { MACOMB_CITIES, OAKLAND_CITIES } from "@/data/cities";
import { COMPANY } from "@/data/site";
import { setSeo, buildLocalBusinessSchema, buildBreadcrumbSchema } from "@/lib/seo";

export default function ServiceAreasPage() {
  useEffect(() => {
    setSeo({
      title: "Plumber Service Areas · Peak Plumbing — Macomb & Oakland Counties, MI",
      description:
        "Peak Plumbing serves every city in Macomb & Oakland County, MI — same-day plumbing, drain, sewer & water heater service. Find your city and call (586) 351-6271.",
      path: "/service-areas",
      jsonLd: [
        {
          id: "org",
          data: buildLocalBusinessSchema({
            areaServed: ["Macomb County, MI", "Oakland County, MI"],
          }),
        },
        {
          id: "breadcrumbs",
          data: buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Service Areas", path: "/service-areas" },
          ]),
        },
      ],
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="service-areas-page" className="bg-white">
      <Navbar />

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-14 md:pt-20 pb-12">
          <div className="label-eyebrow">Service areas</div>
          <h1 className="font-display text-5xl md:text-6xl font-black tracking-tight text-[#1E3A5F] mt-2 max-w-3xl leading-[1]">
            Every city in Macomb <span className="text-[#A88852]">&amp; Oakland Counties.</span>
          </h1>
          <p className="text-slate-600 md:text-lg mt-5 max-w-2xl">
            Peak Plumbing dispatches from Roseville across the whole region — over {MACOMB_CITIES.length + OAKLAND_CITIES.length} cities and townships served. Click your city for local info and same-day scheduling.
          </p>
          <a
            href={`tel:${COMPANY.phoneTel}`}
            className="btn-cta mt-7 inline-flex items-center gap-2 px-5 py-3"
          >
            <Phone size={16} /> Call {COMPANY.phone}
          </a>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-14">
            {[
              { label: "Macomb County", list: MACOMB_CITIES },
              { label: "Oakland County", list: OAKLAND_CITIES },
            ].map((group) => (
              <div key={group.label}>
                <div className="label-eyebrow">{group.label}</div>
                <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-[#1E3A5F] mt-2">
                  {group.list.length} cities & townships in {group.label}.
                </h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-2">
                  {group.list.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/service-areas/${c.slug}`}
                      data-testid={`service-area-link-${c.slug}`}
                      className="group bg-slate-50 border border-slate-200 rounded-md p-3.5 flex items-start justify-between hover:border-[#C5A572] hover:bg-white transition"
                    >
                      <div>
                        <div className="flex items-center gap-2 font-display font-bold text-[#1E3A5F] text-sm">
                          <MapPin size={13} className="text-[#C5A572]" /> {c.name}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          ZIP {c.zip} · Same-day service
                        </div>
                      </div>
                      <ArrowRight
                        size={14}
                        className="text-[#A88852] mt-1 group-hover:translate-x-1 transition"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
